import React, { useState, useEffect } from 'react';
import { ShieldAlert, ShieldCheck, KeyRound, Cog } from 'lucide-react';
import { api } from '../api';

const SOURCE_META = {
  sso: { label: 'LevelUp SSO', className: 'bg-levelup-accent/15 text-levelup-darker border-levelup-accent/40', icon: ShieldCheck },
  local_register: { label: 'Local Register', className: 'bg-amber-100 text-amber-700 border-amber-300', icon: KeyRound },
  admin_seed: { label: 'Admin Seed', className: 'bg-purple-100 text-purple-700 border-purple-300', icon: Cog },
  unknown: { label: 'Unknown', className: 'bg-gray-100 text-gray-600 border-gray-300', icon: ShieldAlert },
};

const AccountAuditPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await api.request('/users');
        setUsers(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const flaggedCount = users.filter(u => u.flagged).length;
  const displayed = users
    .filter(u => !sourceFilter || u.created_via === sourceFilter)
    .filter(u => !search || u.email.toLowerCase().includes(search.toLowerCase()) || (u.domain || '').toLowerCase().includes(search.toLowerCase()));

  const sourceCounts = users.reduce((acc, u) => {
    acc[u.created_via] = (acc[u.created_via] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="account-audit-page">
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <h1 className="text-xl font-bold">Account Audit</h1>
        <p className="text-gray-300 mt-1">See how every account was created — SSO, local register, or admin seed</p>
      </div>

      {flaggedCount > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-4 flex items-start gap-3" data-testid="audit-flagged-banner">
          <ShieldAlert className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-red-800 font-semibold text-sm">{flaggedCount} account{flaggedCount > 1 ? 's' : ''} flagged for review</p>
            <p className="text-red-700 text-sm mt-0.5">These accounts show <code className="px-1 py-0.5 bg-red-100 rounded text-xs font-mono">Local Register</code> as their source but their domain is not in the allowlisted self-registration domains — worth a quick look, especially for accounts created before this audit trail existed (source is a best-effort backfill for those).</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(SOURCE_META).map(([key, meta]) => (
          <div key={key} className="bg-white rounded-xl shadow-sm border p-4" data-testid={`audit-source-count-${key}`}>
            <p className="text-2xl font-bold text-levelup-dark">{sourceCounts[key] || 0}</p>
            <p className="text-sm text-gray-500 mt-1">{meta.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b flex flex-wrap gap-3">
          <input
            className="input-field max-w-xs"
            placeholder="Search email or domain..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="audit-search-input"
          />
          <select
            className="input-field max-w-xs"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            data-testid="audit-source-filter"
          >
            <option value="">All Sources</option>
            {Object.entries(SOURCE_META).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : displayed.length === 0 ? (
          <div className="p-12 text-center text-gray-500" data-testid="audit-empty-state">No accounts match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">User</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Domain</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Role</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Source</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Created</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Last Login</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {displayed.map(u => {
                  const meta = SOURCE_META[u.created_via] || SOURCE_META.unknown;
                  const Icon = meta.icon;
                  return (
                    <tr key={u.id} data-testid={`audit-row-${u.id}`} className={u.flagged ? 'bg-red-50' : ''}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-levelup-dark">{u.full_name}</p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{u.domain}</td>
                      <td className="px-3 py-2 text-gray-600">{u.role}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.className}`} data-testid={`audit-source-badge-${u.id}`}>
                          <Icon size={12} /> {meta.label}
                        </span>
                        {u.flagged && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300" data-testid={`audit-flag-badge-${u.id}`}>
                            <ShieldAlert size={12} /> Review
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {u.last_login_at
                          ? new Date(u.last_login_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                          : <span className="text-gray-400 italic">Never</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountAuditPage;
